import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

/**
 * Superfície elevada do dashboard. Um degrau acima do canvas, borda sutil.
 */
export function Painel({
  children,
  className,
  ...resto
}: {
  readonly children: ReactNode;
  readonly className?: string;
} & HTMLAttributes<HTMLElement>) {
  return (
    <section
      {...resto}
      className={cn(
        "dashboard-painel rounded-xl border border-border-subtle bg-card text-card-foreground",
        className,
      )}
    >
      {children}
    </section>
  );
}

export function PainelCabecalho({
  titulo,
  descricao,
  acao,
  className,
}: {
  readonly titulo: string;
  readonly descricao?: string;
  readonly acao?: ReactNode;
  readonly className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-start justify-between gap-3 border-b border-border/60 px-5 py-4 sm:px-6",
        className,
      )}
    >
      <div className="min-w-0 space-y-1.5">
        <h2 className="text-base font-semibold tracking-[-0.02em] text-foreground">
          {titulo}
        </h2>
        {descricao ? (
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {descricao}
          </p>
        ) : null}
      </div>
      {acao ? <div className="shrink-0">{acao}</div> : null}
    </div>
  );
}

export function PainelCorpo({
  children,
  className,
  semPadding = false,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly semPadding?: boolean;
}) {
  return (
    <div className={cn(semPadding ? "pb-2" : "px-5 py-5 sm:px-6 sm:py-6", className)}>
      {children}
    </div>
  );
}

/** Número de destaque: tipografia densa, tabular. */
export function NumeroHero({
  children,
  className,
  tom = "padrao",
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly tom?: "padrao" | "alerta" | "marca";
}) {
  return (
    <p
      className={cn(
        "font-[family-name:var(--rk-font-display,inherit)] text-4xl font-semibold leading-none tracking-[-0.04em] tabular-nums sm:text-5xl",
        tom === "alerta" && "text-destructive",
        tom === "marca" && "text-[color:var(--rk-text-brand)]",
        tom === "padrao" && "text-foreground",
        className,
      )}
    >
      {children}
    </p>
  );
}

export function MetricaCompacta({
  rotulo,
  valor,
  detalhe,
  delta,
  alerta = false,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly detalhe?: string;
  readonly delta?: string;
  readonly alerta?: boolean;
}) {
  return (
    <div className="dashboard-metrica-compacta flex min-w-0 flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {rotulo}
      </p>
      <div className="flex items-baseline gap-2">
        <p
          className={cn(
            "text-2xl font-semibold tracking-[-0.03em] tabular-nums",
            alerta ? "text-destructive" : "text-foreground",
          )}
        >
          {valor}
        </p>
        {delta && delta !== "n/d" ? (
          <span
            className={cn(
              "text-xs font-medium tabular-nums",
              delta.startsWith("+")
                ? "text-[color:var(--rk-text-brand)]"
                : "text-muted-foreground",
            )}
          >
            {delta}
          </span>
        ) : null}
      </div>
      {detalhe ? (
        <p className="text-sm text-muted-foreground">{detalhe}</p>
      ) : null}
    </div>
  );
}

export function FaixaAlerta({
  titulo,
  descricao,
  severidade,
  acao,
}: {
  readonly titulo: string;
  readonly descricao: string;
  readonly severidade: "alta" | "media" | "baixa";
  readonly acao?: ReactNode;
}) {
  const alta = severidade === "alta";
  return (
    <div
      className={cn(
        "dashboard-alerta relative flex flex-col gap-3 overflow-hidden rounded-[var(--rk-radius-lg,1rem)] border px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6",
        alta
          ? "border-destructive/35 bg-destructive/[0.07]"
          : "border-border/80 bg-[color:var(--rk-bg-brand-soft)]/40",
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute inset-y-3 left-0 w-px rounded-full",
          alta ? "bg-destructive" : "bg-[color:var(--rk-text-brand)]",
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 space-y-2 pl-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={alta ? "destructive" : "secondary"}>
            {alta ? "Urgente" : "Atenção"}
          </Badge>
          <p className="text-sm font-semibold tracking-tight text-foreground">
            {titulo}
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {descricao}
        </p>
      </div>
      {acao ? <div className="shrink-0 pl-2 sm:pl-0">{acao}</div> : null}
    </div>
  );
}

export function BarraCapacidade({
  valor,
  rotulo,
  alerta = false,
}: {
  readonly valor: number;
  readonly rotulo: string;
  readonly alerta?: boolean;
}) {
  const pct = Math.min(100, Math.max(0, valor));
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-3">
        <span className="text-sm text-muted-foreground">{rotulo}</span>
        <span
          className={cn(
            "text-sm font-semibold tabular-nums",
            alerta ? "text-destructive" : "text-foreground",
          )}
        >
          {Math.round(valor)}%
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-hatch"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={rotulo}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none",
            alerta ? "bg-destructive" : "bg-primary",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function ChipStatus({
  children,
  tom = "neutro",
}: {
  readonly children: ReactNode;
  readonly tom?: "neutro" | "ok" | "alerta" | "marca";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        tom === "neutro" && "border-border bg-muted/60 text-foreground",
        tom === "ok" &&
          "border-transparent bg-secondary text-secondary-foreground",
        tom === "alerta" &&
          "border-transparent bg-destructive text-destructive-foreground",
        tom === "marca" &&
          "border-transparent bg-[color:var(--rk-bg-brand-soft,var(--muted))] text-[color:var(--rk-text-brand)]",
      )}
    >
      {children}
    </span>
  );
}
